/**
 * カメラ設定に関するユーティリティHook
 */

interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isFirefox: boolean;
}

interface SettingsInstructions {
  title: string;
  steps: string[];
  action?: () => void;
}

/**
 * OSとブラウザを検出する
 */
const detectPlatform = (): PlatformInfo => {
  const userAgent = navigator.userAgent.toLowerCase();

  // iOSの検出
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Androidの検出
  const isAndroid = /android/.test(userAgent);

  // ブラウザの検出
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
  const isChrome = /chrome/.test(userAgent) && !/edg/.test(userAgent);
  const isFirefox = /firefox/.test(userAgent);

  return { isIOS, isAndroid, isSafari, isChrome, isFirefox };
};

/**
 * 設定手順を取得する
 */
const getSettingsInstructions = (): SettingsInstructions | null => {
  const { isIOS, isAndroid, isSafari, isChrome } = detectPlatform();

  if (isIOS) {
    if (isSafari) {
      return {
        title: 'カメラへのアクセスを許可する手順',
        steps: [
          '1. iPhoneの「設定」アプリを開く',
          '2. 下にスクロールして「Safari」をタップ',
          '3. 「カメラ」をタップ',
          '4. 「許可」を選択',
          '5. このページをリロードする',
        ],
      };
    } else if (isChrome) {
      return {
        title: 'カメラへのアクセスを許可する手順',
        steps: [
          '1. iPhoneの「設定」アプリを開く',
          '2. 下にスクロールして「Chrome」をタップ',
          '3. 「カメラ」をタップ',
          '4. 「許可」を選択',
          '5. このページをリロードする',
        ],
      };
    }
  } else if (isAndroid) {
    if (isChrome) {
      return {
        title: 'カメラへのアクセスを許可する手順',
        steps: [
          '1. 「設定」をタップ',
          '2. Chrome設定が開きます',
          '3. 「サイトの設定」→「カメラ」',
          '4. このサイトを許可に変更',
        ],
        action: () => window.open('chrome://settings/content/camera', '_self'),
      };
    } else {
      return {
        title: 'カメラへのアクセスを許可する手順',
        steps: [
          '1. 端末の「設定」アプリを開く',
          '2. 「アプリ」→「ブラウザ」を選択',
          '3. 「権限」→「カメラ」をタップ',
          '4. 「許可」を選択',
          '5. このページをリロードする',
        ],
      };
    }
  }

  return null;
};

/**
 * カメラ設定に関する機能を提供するHook
 */
export const useCameraSettings = () => {
  const platformInfo = detectPlatform();

  /**
   * カメラ設定画面を開く、または設定手順を表示する
   */
  const openSettings = () => {
    const instructions = getSettingsInstructions();
    if (instructions?.action) {
      instructions.action();
    } else {
      // iOSやその他の場合はアラートで手順を表示
      if (instructions) {
        alert(`${instructions.title}\n\n${instructions.steps.join('\n')}`);
      }
    }
  };

  /**
   * カメラエラー時に設定ボタンを表示すべきかどうか
   */
  const shouldShowSettingsButton = (hasError: boolean): boolean => {
    return (platformInfo.isIOS || platformInfo.isAndroid) && hasError;
  };

  return {
    platformInfo,
    openSettings,
    shouldShowSettingsButton,
  };
};
